import React, { useEffect, useState } from "react";

import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { Box, Grid, Paper, TextField } from "@mui/material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { colors } from "@/theme/overrides/colors";
import { muiError, muiField } from "@/utils/hookForm";
import Spacer from "@components/layout/Spacer";

import { FormValues } from "../RegisterPage";
import AddRemoveButton from "./AddRemoveButton";
import Footer from "./Footer";
import Header from "./Header";

interface Props {
  onNextStep: () => void;
  onPreviousStep: () => void;
}

const Step3Form: React.FC<Props> = ({ onNextStep, onPreviousStep }) => {
  const [year, setYear] = useState("");
  const [name, setName] = useState("");

  const { trigger, setValue } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray<FormValues>({
    name: "trainings",
  });

  // Trigger validation for newly added values to make validation errors appear
  // immediately.
  useEffect(() => {
    trigger(`trainings.${fields.length - 1}`);
  }, [fields.length]);

  const handleNext = async () => {
    // dont go to next step if this form has errors
    const valid = await trigger("trainings");
    if (valid) {
      onNextStep();
    }
  };

  const handleSkip = async () => {
    // when skipped, clear out already added values so that they aren't included
    // in the form values.
    setValue("trainings", []);
    onNextStep();
  };

  return (
    <Paper elevation={0} square>
      <Box padding={8} paddingBottom={12}>
        <Header step={3} />
        <Spacer pxVertical={50} />
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Box
              fontSize={18}
              lineHeight={1}
              fontWeight={700}
              letterSpacing={-0.5}
              color={colors.dark}
            >
              Trainings Attended
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={4}>
              <Grid item xs={3}>
                <TextField
                  type="number"
                  placeholder="Year"
                  fullWidth
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                />
              </Grid>
              <Grid item xs={8}>
                <TextField
                  placeholder="Training name"
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={1}>
                <AddRemoveButton
                  variant="contained"
                  disableElevation
                  onClick={() => {
                    append({ name, year: parseInt(year) });
                    setYear("");
                    setName("");
                  }}
                >
                  <AddIcon />
                </AddRemoveButton>
              </Grid>
              {fields.map((e, index) => (
                <React.Fragment key={e.id}>
                  <Grid item xs={3}>
                    <Controller
                      name={`trainings.${index}.year`}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...muiField(field)}
                          {...muiError(fieldState)}
                          type="number"
                          placeholder="Year"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <Controller
                      name={`trainings.${index}.name`}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...muiField(field)}
                          {...muiError(fieldState)}
                          placeholder="Training name"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={1}>
                    <AddRemoveButton
                      variant="contained"
                      disableElevation
                      onClick={() => remove(index)}
                    >
                      <RemoveIcon />
                    </AddRemoveButton>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Grid>
        </Grid>
        <Spacer pxVertical={60} />
        <Footer
          onNext={handleNext}
          onSkip={handleSkip}
          onBack={onPreviousStep}
        />
      </Box>
    </Paper>
  );
};

export default Step3Form;
